import L from 'leaflet';
import { ZoneCollection, SchoolAssignment, SchoolCollection } from '../types';

/**
 * Calculate the bounding box from GeoJSON data
 */
export function calculateBounds(zones: ZoneCollection): L.LatLngBounds | null {
  if (!zones || !zones.features || zones.features.length === 0) {
    return null;
  }

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  zones.features.forEach(feature => {
    if (feature.geometry.type === 'MultiPolygon') {
      feature.geometry.coordinates.forEach(polygon => {
        polygon.forEach(ring => {
          ring.forEach(coord => {
            const [lng, lat] = coord;
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
          });
        });
      });
    }
  });

  if (minLat === Infinity) {
    return null;
  }

  return L.latLngBounds(
    L.latLng(minLat, minLng),
    L.latLng(maxLat, maxLng)
  );
}

/**
 * Get optimal fit bounds options for Frisco ISD area
 */
export function getFitBoundsOptions(): L.FitBoundsOptions {
  return {
    padding: [20, 20], // 20px padding on all sides
    maxZoom: 13,       // Don't zoom in too much
  };
}

/**
 * Check if a point is inside a polygon using ray casting algorithm
 */
function pointInPolygon(point: [number, number], polygon: number[][]): boolean {
  const [x, y] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Check if a point is inside a MultiPolygon geometry
 */
function pointInMultiPolygon(point: [number, number], multiPolygon: number[][][][]): boolean {
  // Point is in format [lng, lat], coordinates are in format [lng, lat]
  const testPoint: [number, number] = [point[0], point[1]];

  for (const polygon of multiPolygon) {
    // Check the outer ring (first ring)
    const outerRing = polygon[0];
    if (pointInPolygon(testPoint, outerRing)) {
      // Check if it's in any holes (inner rings)
      let inHole = false;
      for (let i = 1; i < polygon.length; i++) {
        const hole = polygon[i];
        if (pointInPolygon(testPoint, hole)) {
          inHole = true;
          break;
        }
      }
      
      if (!inHole) {
        return true; // Point is in polygon and not in any hole
      }
    }
  }

  return false;
}

/**
 * Find which school zones contain a given point
 */
export function findSchoolAssignment(
  lat: number,
  lng: number,
  zones: ZoneCollection
): SchoolAssignment {
  const point: [number, number] = [lng, lat]; // GeoJSON uses [lng, lat] format
  
  const assignment: SchoolAssignment = {
    zones: {}
  };

  // Check each zone to see if the point is inside
  zones.features.forEach(zone => {
    if (zone.geometry.type === 'MultiPolygon') {
      if (pointInMultiPolygon(point, zone.geometry.coordinates)) {
        // Found a zone that contains this point
        const schoolType = zone.properties.type;
        
        if (schoolType === 'Elementary') {
          assignment.zones.elementary = zone;
        } else if (schoolType === 'Middle School') {
          assignment.zones.middle = zone;
        } else if (schoolType === 'High School') {
          assignment.zones.high = zone;
        }
      }
    }
  });

  return assignment;
}

/**
 * Format school assignment for display
 */
export function formatSchoolAssignment(assignment: SchoolAssignment): string {
  const schools = [];
  
  if (assignment.zones.elementary) {
    schools.push(`Elementary: ${assignment.zones.elementary.properties.name}`);
  }
  
  if (assignment.zones.middle) {
    schools.push(`Middle: ${assignment.zones.middle.properties.name}`);
  }
  
  if (assignment.zones.high) {
    schools.push(`High: ${assignment.zones.high.properties.name}`);
  }

  if (schools.length === 0) {
    return 'No school zones found for this location';
  }

  return schools.join('\n');
}

/**
 * Get coordinates for assigned schools
 */
export function getAssignedSchoolCoordinates(
  assignment: SchoolAssignment,
  schools: SchoolCollection
): Array<{name: string; lat: number; lng: number; type: string}> {
  const coordinates: Array<{name: string; lat: number; lng: number; type: string}> = [];
  
  // Create a map of school names to coordinates for fast lookup
  const schoolMap = new Map();
  schools.features.forEach(school => {
    schoolMap.set(school.properties.name, {
      lat: school.geometry.coordinates[1], // latitude
      lng: school.geometry.coordinates[0], // longitude
      type: school.properties.type
    });
  });
  
  // Get coordinates for each assigned school
  if (assignment.zones.elementary) {
    const coords = schoolMap.get(assignment.zones.elementary.properties.name);
    if (coords) {
      coordinates.push({
        name: assignment.zones.elementary.properties.name,
        lat: coords.lat,
        lng: coords.lng,
        type: 'Elementary'
      });
    }
  }
  
  if (assignment.zones.middle) {
    const coords = schoolMap.get(assignment.zones.middle.properties.name);
    if (coords) {
      coordinates.push({
        name: assignment.zones.middle.properties.name,
        lat: coords.lat,
        lng: coords.lng,
        type: 'Middle School'
      });
    }
  }
  
  if (assignment.zones.high) {
    const coords = schoolMap.get(assignment.zones.high.properties.name);
    if (coords) {
      coordinates.push({
        name: assignment.zones.high.properties.name,
        lat: coords.lat,
        lng: coords.lng,
        type: 'High School'
      });
    }
  }
  
  return coordinates;
} 